/**
 * Интеграции: шесть внешних сервисов, у каждого свой провайдер
 * (`off` · `stub` · `real`), журнал обмена и проверка связи.
 *
 * Ключей и секретов API не отдаёт вовсе — они живут в окружении сервера.
 * Поэтому экран показывает, **на чём работает** интеграция и что она делала,
 * а не поля с паролями (BR-47).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { parseMs as toMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type IntegrationDto = components['schemas']['Integration']
type ExchangeDto = components['schemas']['Exchange']

export interface Integration {
  key: string
  title: string
  /** На чём сейчас работает: `off`, `stub` или `real`. */
  provider: string
  options: string[]
  enabled: boolean
  /** Что нужно, чтобы заработал «real»: ключи, договор, бот. */
  note: string
  /** Имя настройки, которой переключается провайдер. */
  setting: string
}

export interface Exchange {
  id: number
  key: string
  provider: string
  operation: string
  ok: boolean
  message: string
  storeId: number
  orderId: number
  at: number
}

/**
 * «Карты и адреса» — это Яндекс Карты и геокодер в браузере покупателя, а не
 * серверный провайдер: включены или нет. Встроенная заглушка сервера (`stub`)
 * отвечает на `/geo/*`, которые приложения не вызывают, поэтому на экране её
 * нет — она показывается настоящим провайдером: карты при ней работают.
 */
const GEO_KEY = 'geo'

function geoProvider(provider: string): string {
  return provider === 'stub' ? 'real' : provider
}

export function toIntegration(dto: IntegrationDto): Integration {
  const key = dto.key ?? ''
  const provider = dto.provider ?? ''
  const options = dto.options ?? []
  const geo = key === GEO_KEY
  return {
    key,
    title: dto.title ?? '',
    provider: geo ? geoProvider(provider) : provider,
    options: geo ? options.filter((option) => option !== 'stub') : options,
    enabled: dto.enabled ?? false,
    note: dto.note ?? '',
    setting: dto.setting ?? '',
  }
}

export function toExchange(dto: ExchangeDto): Exchange {
  return {
    id: dto.id ?? 0,
    key: dto.key ?? '',
    provider: dto.provider ?? '',
    operation: dto.operation ?? '',
    ok: dto.status !== 'error',
    message: dto.message ?? '',
    storeId: dto.store_id ?? 0,
    orderId: dto.order_id ?? 0,
    at: toMs(dto.created_at),
  }
}

export async function fetchIntegrations(api: ApiClient): Promise<Integration[]> {
  const page = await api.get<{ items?: IntegrationDto[] }>('/admin/integrations')
  return (page.items ?? []).map(toIntegration)
}

export function useIntegrations() {
  const api = useApi()
  return useQuery({ queryKey: keys.integrations, queryFn: () => fetchIntegrations(api) })
}

/** Страница журнала обмена: листается на сервере, по 10 строк. */
export interface ExchangePage {
  items: Exchange[]
  total: number
}

/** Журнал: без ключа — обмены всех интеграций, с ключом — одной. */
export function useExchangeLog(key: string, page = 1, pageSize = 10) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.integrationLog(key), page, pageSize],
    queryFn: async (): Promise<ExchangePage> => {
      const path = key ? `/admin/integrations/${key}/log` : '/admin/integrations/log'
      const dto = await api.get<{ items?: ExchangeDto[]; total?: number }>(path, {
        query: { page, page_size: pageSize },
      })
      return { items: (dto.items ?? []).map(toExchange), total: dto.total ?? 0 }
    },
    placeholderData: keepPreviousData,
  })
}

export function useSetProvider() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ key, provider }: { key: string; provider: string }) => {
      const page = await api.put<{ items?: IntegrationDto[] }>(`/admin/integrations/${key}`, {
        provider,
      })
      return (page.items ?? []).map(toIntegration)
    },
    onSuccess: async (items) => {
      client.setQueryData(keys.integrations, items)
      // Провайдер платежей — это `mode` каналов оплаты.
      await client.invalidateQueries({ queryKey: keys.paymentChannels })
    },
  })
}

export interface TestResult {
  ok: boolean
  message: string
}

export function useTestIntegration() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (key: string) => {
      const done = await api.post<{ ok?: boolean; message?: string }>(
        `/admin/integrations/${key}/test`,
      )
      return { ok: done.ok ?? false, message: done.message ?? '' } satisfies TestResult
    },
    onSuccess: (_result, key) => client.invalidateQueries({ queryKey: keys.integrationLog(key) }),
  })
}

export interface FomStore {
  storeId: number
  storeName: string
  /** Логин кассы; пустая строка — доступ ещё не выдан. */
  login: string
  branch: string
  issued: number
  lastStock: number
  lastStockRows: number
  lastReceipt: number
}

/** Состояние обмена с учётной системой по каждой аптеке. */
export function useFomStores(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: keys.fom,
    enabled,
    queryFn: async () => {
      const page = await api.get<{
        items?: {
          store_id?: number
          store_name?: string
          till_login?: string | null
          fom_branch?: string | null
          credentials_issued_at?: string | null
          last_stock_at?: string | null
          last_stock_rows?: number | null
          last_receipt_at?: string | null
        }[]
      }>('/admin/integrations/fom')
      return (page.items ?? []).map<FomStore>((i) => ({
        storeId: i.store_id ?? 0,
        storeName: i.store_name ?? '',
        login: i.till_login ?? '',
        branch: i.fom_branch ?? '',
        issued: toMs(i.credentials_issued_at),
        lastStock: toMs(i.last_stock_at),
        lastStockRows: i.last_stock_rows ?? 0,
        lastReceipt: toMs(i.last_receipt_at),
      }))
    },
  })
}

/** Доступы аптеки к нашему API для программ FOM — сервер отдаёт их один раз. */
export interface FomSecrets {
  storeId: number
  /** Логин кассы (CASHBACK.exe), всегда `apteka-<id>`. */
  tillLogin: string
  tillPassword: string
  /** Токен SITE.exe — строка `TOCING=` в SETUP.ini. */
  siteToken: string
}

/**
 * Выдать или перевыпустить доступы аптеки. Старые сразу перестают работать,
 * кассы аптеки выходят из системы.
 *
 * Пароль и токен на сервере хранятся только хэшами, повторно их не получить.
 * Поэтому ответ не кладём ни в кэш запросов, ни в хранилище: `gcTime: 0`
 * убирает его из кэша мутаций, как только окно перестаёт его показывать.
 */
export function useIssueFomCredentials() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    gcTime: 0,
    mutationFn: async (storeId: number) => {
      const done = await api.post<{
        store_id?: number
        till_login?: string
        till_password?: string
        site_token?: string
      }>(`/admin/stores/${storeId}/fom-credentials`)
      return {
        storeId: done.store_id ?? storeId,
        tillLogin: done.till_login ?? '',
        tillPassword: done.till_password ?? '',
        siteToken: done.site_token ?? '',
      } satisfies FomSecrets
    },
    // Логин и «Выданы» обновились — перечитываем состояние, без секретов.
    onSuccess: () => client.invalidateQueries({ queryKey: keys.fom }),
  })
}

/** Повторно применить последние файлы остатков: наружу ничего не уходит. */
export function useFomSync() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const done = await api.post<{ pharmacies?: number; rows?: number }>(
        '/admin/integrations/fom/sync',
      )
      return { stores: done.pharmacies ?? 0, rows: done.rows ?? 0 }
    },
    // Остатки применены заново — устарели и журнал обмена, и остатки товаров.
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.integrations }),
        client.invalidateQueries({ queryKey: ['products'] }),
      ]),
  })
}

type ChannelsDto = components['schemas']['PaymentChannels']

export type ChannelKey = 'wlcm' | 'cards' | 'rahmat'
/** Какая страница оплаты берёт карту, Click, Payme и Uzum, когда включены обе. */
export type MainChannel = 'wlcm' | 'rahmat'

export interface PaymentChannel {
  key: ChannelKey
  title: string
  enabled: boolean
  /** Ключи шлюза лежат на сервере; на заглушке — всегда да. */
  connected: boolean
  /** Способы оплаты, которые канал приносит покупателю. */
  methods: string[]
}

/** Каналы онлайн-оплаты (R-113): три переключателя и главная страница оплаты. */
export interface PaymentChannels {
  /** `integration.payment.provider`: off · stub · real. */
  mode: string
  main: MainChannel
  channels: PaymentChannel[]
}

/** Что сохраняет супер-админ: каждый канал и главная страница. */
export type ChannelsDraft = Record<ChannelKey, boolean> & { main: MainChannel }

export function toChannels(dto: ChannelsDto): PaymentChannels {
  return {
    mode: dto.mode ?? 'off',
    main: dto.main ?? 'wlcm',
    channels: (dto.channels ?? []).map((c) => ({
      key: c.key ?? 'wlcm',
      title: c.title ?? '',
      enabled: c.enabled ?? false,
      connected: c.connected ?? false,
      methods: c.methods ?? [],
    })),
  }
}

/** Черновик из того, что сейчас на сервере. */
export function channelsDraft(channels: PaymentChannels): ChannelsDraft {
  const on = (key: ChannelKey) => channels.channels.some((c) => c.key === key && c.enabled)
  return { wlcm: on('wlcm'), cards: on('cards'), rahmat: on('rahmat'), main: channels.main }
}

export function usePaymentChannels(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: keys.paymentChannels,
    queryFn: async () => toChannels(await api.get<ChannelsDto>('/admin/payment/channels')),
    enabled,
  })
}

/** Переключает только супер-админ; остальным сервер ответит 403. */
export function useSetPaymentChannels() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (draft: ChannelsDraft) =>
      toChannels(await api.put<ChannelsDto>('/admin/payment/channels', draft)),
    onSuccess: (channels) => {
      client.setQueryData(keys.paymentChannels, channels)
    },
  })
}

type MapKeyDto = components['schemas']['MapKey']

export type MapKeyStatus = 'unknown' | 'ok' | 'exhausted' | 'invalid'

/** Ключ Яндекс Карт из пула: браузерный, поэтому админ видит его целиком. */
export interface MapKey {
  id: number
  label: string
  apiKey: string
  active: boolean
  /** Что ответил Яндекс при последней проверке. */
  status: MapKeyStatus
  /** До какого момента лимит исчерпан (полночь по Москве); 0 — не исчерпан. */
  exhaustedUntil: number
  checkedAt: number
  /** Порядок выдачи: меньше — раньше. */
  sort: number
}

export function toMapKey(dto: MapKeyDto): MapKey {
  return {
    id: dto.id ?? 0,
    label: dto.label ?? '',
    apiKey: dto.api_key ?? '',
    active: dto.is_active ?? false,
    status: dto.status ?? 'unknown',
    exhaustedUntil: toMs(dto.exhausted_until),
    checkedAt: toMs(dto.checked_at),
    sort: dto.sort ?? 0,
  }
}

/** Пул ключей Яндекс Карт в порядке выдачи (право `integrations:r`). */
export function useMapKeys() {
  const api = useApi()
  return useQuery({
    queryKey: keys.mapKeys,
    queryFn: async () => {
      const page = await api.get<{ items?: MapKeyDto[] }>('/admin/maps/keys')
      return (page.items ?? []).map(toMapKey)
    },
  })
}

export interface NewMapKey {
  label: string
  apiKey: string
  sort: number
}

export function useAddMapKey() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (draft: NewMapKey) =>
      toMapKey(
        await api.post<MapKeyDto>('/admin/maps/keys', {
          label: draft.label.trim(),
          api_key: draft.apiKey.trim(),
          is_active: true,
          sort: draft.sort,
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.mapKeys }),
  })
}

/**
 * Сохраняет ключи целиком: сервер перезаписывает и подпись, и порядок, так что
 * без них он стёр бы подпись и поставил ключ первым. Сам ключ не меняется —
 * его удаляют и добавляют новый.
 */
export function useSaveMapKeys() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (list: MapKey[]) => {
      for (const key of list) {
        await api.put<unknown>(`/admin/maps/keys/${key.id}`, {
          label: key.label,
          is_active: key.active,
          sort: key.sort,
        })
      }
    },
    onSettled: () => client.invalidateQueries({ queryKey: keys.mapKeys }),
  })
}

export function useDeleteMapKey() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.del<unknown>(`/admin/maps/keys/${id}`)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.mapKeys }),
  })
}

/** Спросить Яндекс прямо сейчас: тратит один запрос из дневного лимита ключа. */
export function useCheckMapKey() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) =>
      toMapKey(await api.post<MapKeyDto>(`/admin/maps/keys/${id}/check`)),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.mapKeys }),
  })
}
